import { useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import Icon from "Components/Icon/Icon";
import useTerms from "hooks/useTerms";

import styles from "./TermsBanner.module.scss";

/**
 * Modal con el texto de los T&C + checkbox + Aceptar. Compartido por el banner
 * ("Ver términos") y por el modal de feature bloqueada (TermsLockModal). El
 * texto viene como HTML sanitizado con DOMPurify.
 */
export default function TermsAcceptModal({ show, onHide }) {
  const { t } = useTranslation();
  const { current, accept, isAccepting } = useTerms();
  const [checked, setChecked] = useState(false);

  const title = current?.title || t("terms_modal_title", "Términos y condiciones de las funciones con IA");
  const effectiveMs = current?.effectiveFrom != null ? Number(current.effectiveFrom) : null;
  const effectiveDate = Number.isFinite(effectiveMs)
    ? new Date(effectiveMs).toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  const confirmAccept = () => {
    accept();
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} scrollable centered size="lg" contentClassName={styles.modalContent}>
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-2">
          <Icon name="article" size={24} className={styles.icon} />
          <div>
            <Modal.Title as="h5" className={styles.title}>{title}</Modal.Title>
            {effectiveDate && (
              <div className={`small ${styles.subtitle}`}>
                {t("terms_modal_effective", "Vigentes desde el {{date}}.", { date: effectiveDate })}
              </div>
            )}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.modalBody} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(current?.body || "") }} />
      </Modal.Body>
      <Modal.Footer className="flex-column align-items-stretch gap-3">
        <label className={`d-flex align-items-center gap-2 ${styles.check}`}>
          <input type="checkbox" className="form-check-input m-0" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          {t("terms_modal_checkbox", "Leí y acepto los términos y condiciones de las funciones con IA")}
        </label>
        <div className="d-flex gap-2">
          <button type="button" className={`flex-fill ${styles.secondaryBtn}`} onClick={onHide}>
            {t("terms_modal_later", "Ahora no")}
          </button>
          <button type="button" className={`flex-fill ${styles.primaryBtn}`} disabled={!checked || isAccepting} onClick={confirmAccept}>
            {isAccepting ? t("terms_modal_accepting", "Aceptando…") : t("terms_modal_accept", "Aceptar")}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
